// workDays.mjs — pure YYYYMMDD calendar-label helpers for the work-hours surface
// (SB-034). No React, no DOM, no Date-now — unit-testable.
//
// WHY labels and not instants: a work day is a LABEL in the owner's timezone
// (P-03/P-08 — the backend stores `work_day` as YYYYMMDD plus a tz snapshot), so
// all arithmetic here is calendar arithmetic. We use Date.UTC purely as a
// civil-date calculator; no timezone conversion happens or should happen.

// ── SB-159（第三百八十二批）：工作项选择器的提示（口径 2／3）──────────────────────────────
// /api/me/backlogs 自第一百三十一批就给 limit（钳 500）与 hasMore，而三个页面一个都没接：
// 页面不发 limit（服务端缺省 200）、也不看 hasMore。记工时页更糟——取数失败被 `.catch(() => null)`
// 吞成空表，于是印出「当前项目组没有可登记的工单」：**取不到长得与没有一模一样**（口径 3）。
// 判定收在这里：error ⇒ 取不到（绝不是 noBacklogs）；零行 ⇒ 没有；hasMore ⇒ 这一页之外还有。
export const BACKLOG_PICKER_LIMIT = 500;

export function backlogPickerHint({ error = null, rows = [], hasMore = false, limit } = {}) {
  if (error) return { key: 'work.backlogsLoadFailed' };
  if (!Array.isArray(rows) || rows.length === 0) return { key: 'work.noBacklogs' };
  if (hasMore === true) return { key: 'paging.hasMore', params: { n: limit ?? BACKLOG_PICKER_LIMIT } };
  return null;
}

// 复审（N3）：提交页 asset 表单「关联工单号」那只下拉的 helper。它与记工时页的选择器不同：下拉是选填的、
// freeSolo（零个候选就是手输编号，不是「没有」），所以零行不出 noBacklogs；但**取数失败**此前被吞成空表、
// 配一句照常的 helper——取不到长得与「这个组没有工作项」一模一样（口径 3）。
// 返回 helper 依次要说的那几句 [{key, params?}]：失败 ⇒ 先说没取回来（手输仍可）；还有更多 ⇒ 照常那句后面补一句。
export function relatedBacklogHelper({ failed = false, more = null } = {}) {
  const base = { key: 'submit.field.relatedBacklog.helper' };
  if (failed) return [{ key: 'work.backlogsLoadFailed' }, base];
  return more ? [base, more] : [base];
}

export function fromYmd(ymd) {
  const n = Number(ymd);
  return { y: Math.floor(n / 10000), m: Math.floor((n % 10000) / 100), d: n % 100 };
}

export function toYmd({ y, m, d }) {
  return y * 10000 + m * 100 + d;
}

// Shift a YYYYMMDD label by whole days (month/year rollover included).
export function addDays(ymd, delta) {
  const { y, m, d } = fromYmd(ymd);
  const t = Date.UTC(y, m - 1, d) + delta * 86400000;
  const dt = new Date(t);
  return toYmd({ y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() });
}

// The R-01 entry window: today ± (windowDays−1)/2, oldest first. This same span
// is both the legal entry boundary and the week strip the UI renders — one
// concept, not two, which is why the strip can never offer an illegal day.
export function entryWindow(today, windowDays) {
  const n = Number(windowDays);
  if (!Number.isInteger(n) || n < 1 || n % 2 === 0) return [today];
  const half = (n - 1) / 2;
  const out = [];
  for (let i = -half; i <= half; i += 1) out.push(addDays(today, i));
  return out;
}

// 20260727 → "07-27" (compact strip label).
export function shortLabel(ymd) {
  const { m, d } = fromYmd(ymd);
  return `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// 20260727 → "2026-07-27".
export function isoLabel(ymd) {
  const { y, m, d } = fromYmd(ymd);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// Day-of-week index (0=Sun) for a label, via the same civil-date calculator.
export function weekdayIndex(ymd) {
  const { y, m, d } = fromYmd(ymd);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

// Minutes → a compact hours NUMBER string: 90 → "1.5", 120 → "2", 0 → "0".
// Batch 43 (language rule): the unit suffix moved out of this pure module —
// render sites wrap it with t('work.hoursValue', { n }) so zh reads 小时.
export function hoursLabel(minutes) {
  const n = Number(minutes) || 0;
  if (n === 0) return '0';
  const h = n / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)}`;
}

// R-11: 16h/day across all backlogs. Named here (not in the page) so the strip
// label and the validator read the same figure. NOTE: the server does not yet
// send the cap alongside windowDays — a known needs-decision from the batch-29
// scan; until then this mirrors work-hours-rules R-11 verbatim.
export const DAY_CAP_MINUTES = 960;

// R-05 mirror: an hours input is legal iff it is a positive multiple of 0.5h.
// Returns whole MINUTES, or null when the input is not acceptable. SB-042: this
// check used to be duplicated in the entry form and the reversal dialog — the
// reversal copy silently swallowed bad input (no message), which is exactly the
// drift a single validator prevents.
export function hoursToMinutes(input) {
  const h = Number(input);
  if (!Number.isFinite(h) || h <= 0) return null;
  const minutes = Math.round(h * 60);
  return minutes % 30 === 0 ? minutes : null;
}

// Net minutes per day from timesheet rows (Σ minutes×direction over the states
// that count — planned and invested; released/migrated rows are history).
export function netByDay(rows) {
  const out = {};
  for (const r of rows || []) {
    if (r.status !== 'planned' && r.status !== 'invested') continue;
    out[r.workDay] = (out[r.workDay] || 0) + (r.netMinutes ?? r.minutes * r.direction);
  }
  return out;
}
